export interface SupplierSummary {
  id: string
  supplier_id: string
  supplier_name: string
  symbol?: string | null
}

export interface PurchaseRequest {
  id: string
  component_id?: string | null
  component_name?: string | null
  item_name?: string | null
  quantity: number
  description?: string | null
  requested_by_name?: string | null
  purchase_id?: string | null
  folder_id?: string | null
  target_location?: string | null
  target_location_name?: string | null
  source?: { type?: string; bom_id?: string; line_id?: string; production_id?: string } | null
  suppliers?: SupplierSummary[]
  mfpn?: string | null
  matching_supplier_relation_id?: string | null
  created_at: string
}

export interface PurchaseRequestFolder {
  id: string
  name: string
  parent: string | null
  full_path: string
}

export interface FolderNode extends PurchaseRequestFolder {
  children: FolderNode[]
  requests: PurchaseRequest[]
}

export const FOLDER_DRAG_PREFIX = 'folder:'
export const REQUEST_DRAG_PREFIX = 'request:'
export const ROOT_DROP_ID = 'root'
export const UNGROUPED_ID = '__ungrouped__'

/** Position of a request inside a run of requests for the same item within one folder. */
export interface DuplicateInfo {
  index: number
  size: number
  totalQuantity: number
}

export type TreeRow =
  | { kind: 'folder'; node: FolderNode; depth: number }
  | { kind: 'request'; request: PurchaseRequest; depth: number; duplicate?: DuplicateInfo }

const itemKey = (request: PurchaseRequest) =>
  request.component_id ? `c:${request.component_id}` : `n:${(request.item_name || '').trim().toLowerCase()}`

/** Keeps the incoming order, but pulls requests for the same item next to the first one of them. */
function groupDuplicates(requests: PurchaseRequest[]): Array<{ request: PurchaseRequest; duplicate?: DuplicateInfo }> {
  const groups = new Map<string, PurchaseRequest[]>()
  for (const request of requests) {
    const key = itemKey(request)
    const group = groups.get(key)
    if (group) {
      group.push(request)
    } else {
      groups.set(key, [request])
    }
  }
  const result: Array<{ request: PurchaseRequest; duplicate?: DuplicateInfo }> = []
  for (const group of groups.values()) {
    if (group.length === 1) {
      result.push({ request: group[0] })
      continue
    }
    const totalQuantity = group.reduce((sum, request) => sum + (Number(request.quantity) || 0), 0)
    group.forEach((request, index) => {
      result.push({ request, duplicate: { index, size: group.length, totalQuantity } })
    })
  }
  return result
}

/** Flattens folders (+ an "Ungrouped" pseudo-folder for root-level requests) into one
 * ordered list of visible rows, respecting which folder ids are currently expanded. */
export function buildVisibleRows(
  folderTree: FolderNode[],
  ungroupedRequests: PurchaseRequest[],
  expandedIds: Set<string>,
): TreeRow[] {
  const rows: TreeRow[] = []

  const visit = (nodes: FolderNode[], depth: number) => {
    for (const node of nodes) {
      rows.push({ kind: 'folder', node, depth })
      if (expandedIds.has(node.id)) {
        visit(node.children, depth + 1)
        for (const { request, duplicate } of groupDuplicates(node.requests)) {
          rows.push({ kind: 'request', request, depth: depth + 1, duplicate })
        }
      }
    }
  }

  visit(folderTree, 0)

  const ungroupedNode: FolderNode = {
    id: UNGROUPED_ID,
    name: 'Ungrouped',
    parent: null,
    full_path: 'Ungrouped',
    children: [],
    requests: ungroupedRequests,
  }
  rows.push({ kind: 'folder', node: ungroupedNode, depth: 0 })
  if (expandedIds.has(UNGROUPED_ID)) {
    for (const { request, duplicate } of groupDuplicates(ungroupedRequests)) {
      rows.push({ kind: 'request', request, depth: 1, duplicate })
    }
  }

  return rows
}

export function suppliersTooltip(suppliers?: SupplierSummary[]) {
  if (!suppliers || suppliers.length === 0) {
    return "-"
  }
  return suppliers.map((supplier) => supplier.supplier_name).filter(Boolean).join(", ")
}

export function isDescendantFolder(
  folders: PurchaseRequestFolder[],
  candidateId: string,
  ancestorId: string,
): boolean {
  const byId = new Map(folders.map((folder) => [folder.id, folder]))
  let current = byId.get(candidateId)
  while (current?.parent) {
    if (current.parent === ancestorId) {
      return true
    }
    current = byId.get(current.parent)
  }
  return false
}
